import React from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";

import { getConsentDetailsService } from "@app/services/candidate/getConsentDetailsService";
import { updateConsentService } from "@app/services/candidate/updateConsentService";

import { useToast } from "@hooks/useToast";

import { CONSENT_STATUS } from "@utils/enum";
import { diffDays, formatDate } from "@utils/utils";

export const useCandidateConsentStep = () => {
  const { messages } = useIntl();
  const { candidate_id } = useParams();
  const { showErrorToast } = useToast();

  const [orderInfo, setOrderInfo] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);

  const orderDetails = React.useMemo(() => {
    if (!orderInfo) {
      return {
        candidateId: candidate_id,
        companyName: "",
        candidateName: "",
        orderDate: "",
        expiresIn: 0,
        products: [],
        consentStatus: "",
      };
    }

    const orderDate = new Date(orderInfo?.order_date);
    const expiresInDate = new Date(orderInfo?.order_date);
    expiresInDate.setDate(expiresInDate.getDate() + 14);

    return {
      candidateId: candidate_id,
      companyName: orderInfo?.company_name,
      candidateName: orderInfo?.order_items?.[0]?.person_name,
      orderDate: formatDate(orderDate),
      expiresIn: diffDays(expiresInDate),
      products:
        orderInfo?.order_items?.map?.(
          ({ product_name }) => product_name ?? ""
        ) ?? [],
      consentStatus: orderInfo?.consent_status,
    };
  }, [candidate_id, orderInfo]);

  const handleUpdateConsent = React.useCallback(async () => {
    try {
      setIsLoading(true);

      const payload = {
        consent_status: CONSENT_STATUS.COMPLETED,
      };

      const {
        data: { candidate: { consent_status: nextConsentStatus } = {} } = {},
      } = await updateConsentService(candidate_id, payload);

      setOrderInfo(prev => ({
        ...prev,
        consent_status: nextConsentStatus,
      }));
    } catch {
      showErrorToast(messages.exception_error_message);
    } finally {
      setIsLoading(false);
    }
  }, [candidate_id, messages.exception_error_message, showErrorToast]);

  React.useEffect(() => {
    const fetchOrderInfo = async () => {
      try {
        setIsLoading(true);

        const {
          data: { data: orderInfoData },
        } = await getConsentDetailsService(candidate_id);

        setOrderInfo(orderInfoData);
      } catch (error) {
        if (error?.response?.status === 401) {
          localStorage.removeItem("candidateToken");
        }

        showErrorToast(messages.exception_error_message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrderInfo();
  }, [candidate_id, messages.exception_error_message, showErrorToast]);

  return {
    orderDetails,
    handleUpdateConsent,
    isLoading,
  };
};
